/* Audio format helpers: what the browser can decode natively, and a human label for Now Playing. */
const probe = typeof document !== "undefined" ? document.createElement("audio") : null;
const can = (type) => (probe ? probe.canPlayType(type) : "probably");

const BY_TYPE = { "audio/mpeg": "mp3", "audio/flac": "flac", "audio/mp4": "m4a", "audio/aac": "aac", "audio/ogg": "ogg", "audio/wav": "wav", "audio/aiff": "aiff", "audio/x-ms-wma": "wma", "audio/webm": "webm" };

export const codecOf = (t) => {
  if (t?.codec) return t.codec;
  const ext = (t?.original_filename || "").split(".").pop().toLowerCase();
  if (["aiff", "aif"].includes(ext)) return "aiff";
  if (ext === "wma") return "wma";
  if (["aax", "aa"].includes(ext)) return "aax";
  if (ext === "m4p") return "drm";
  return null;
};

/* Audible (.aax/.aa) and iTunes FairPlay (.m4p) files are catalogued but can't be decoded by anything we ship. */
export const isProtected = (t) => !!t && !t.live && ["aax", "drm"].includes(codecOf(t));

/* '' | 'maybe' | 'probably' for the track's native stream. Unknown codecs are assumed playable; the player falls back on error. */
export const nativeSupport = (t) => {
  const c = codecOf(t);
  if (c === "aax" || c === "drm") return "";
  if (c === "alac") return can('audio/mp4; codecs="alac"');
  if (c === "aiff") return can("audio/aiff") || can("audio/x-aiff");
  if (c === "wma") return can("audio/x-ms-wma");
  if (c === "opus") return can('audio/ogg; codecs="opus"') || can("audio/ogg");
  if (c === "vorbis") return can('audio/ogg; codecs="vorbis"') || can("audio/ogg");
  if (c === "flac") return can("audio/flac") || can("audio/x-flac");
  if (c === "aac") return can('audio/mp4; codecs="mp4a.40.2"') || can("audio/aac");
  if (c === "mp3") return can("audio/mpeg");
  return "probably";
};
export const needsTranscode = (t) => !!t && !t.live && !isProtected(t) && nativeSupport(t) === "";

const NAMES = { alac: "ALAC", aac: "AAC", mp3: "MP3", flac: "FLAC", vorbis: "OGG Vorbis", opus: "Opus", pcm: "WAV", aiff: "AIFF", wma: "WMA", aax: "Audible AAX", drm: "iTunes protected" };
export const LOSSLESS = new Set(["alac", "flac", "pcm", "aiff"]);

export const formatLabel = (t) => {
  if (!t || t.live) return null;
  const c = codecOf(t);
  const name = NAMES[c] || (BY_TYPE[t.content_type] || "").toUpperCase() || null;
  if (!name) return null;
  const bits = [];
  if (t.bit_depth && t.sample_rate) bits.push(`${t.bit_depth}-bit / ${(t.sample_rate / 1000).toFixed(t.sample_rate % 1000 ? 1 : 0)} kHz`);
  else if (t.sample_rate) bits.push(`${(t.sample_rate / 1000).toFixed(t.sample_rate % 1000 ? 1 : 0)} kHz`);
  else if (t.bitrate) bits.push(`${Math.round(t.bitrate / 1000)} kbps`);
  return { name, detail: bits[0] || null, lossless: LOSSLESS.has(c), protected: isProtected(t) };
};
